/**
 * Maps search URLs for nearby retail print shops.
 *
 * v1 opens Google Maps with a search. Yona does not pick a store, upload the
 * PDF, or take payment. Hunt-land coordinates are rounded so the exact
 * boundary centroid does not appear in the URL.
 *
 * Maps search does not understand boolean OR. Coordinate searches use the
 * /search/{term}/@{lat},{lng},{zoom}z form so the map actually centres there.
 */

export const PRINT_SHOP_SEARCH = 'print shops';
export const MAPS_SEARCH_ZOOM = 12;

/** ~1.1 km at mid-latitudes. Fine enough to find a town, coarse enough to hide the pin. */
export const LAND_COORD_DECIMALS = 2;

export function roundLandCoordinate(value, decimals = LAND_COORD_DECIMALS) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

export function roundLandCentroid(centroid) {
  if (!centroid) return null;
  const lat = roundLandCoordinate(centroid.lat);
  const lng = roundLandCoordinate(centroid.lng);
  if (lat == null || lng == null) return null;
  return { lat, lng };
}

function coordinates({ lat, lng, roundPlace = false }) {
  const useLat = roundPlace ? roundLandCoordinate(lat) : Number(lat);
  const useLng = roundPlace ? roundLandCoordinate(lng) : Number(lng);
  if (!Number.isFinite(useLat) || !Number.isFinite(useLng)) return null;
  const decimals = roundPlace ? LAND_COORD_DECIMALS : 4;
  return {
    lat: Number(useLat.toFixed(decimals)),
    lng: Number(useLng.toFixed(decimals)),
    label: `${useLat.toFixed(decimals)},${useLng.toFixed(decimals)}`,
  };
}

function placeLabel(place) {
  if (typeof place !== 'string') return '';
  return place.replace(/\s+/g, ' ').trim().slice(0, 80);
}

export function printShopMapsUrl(origin = {}) {
  const typed = placeLabel(origin.place);
  if (typed) {
    const query = `${PRINT_SHOP_SEARCH} near ${typed}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }

  const point = coordinates(origin);
  if (!point) return '';
  const term = encodeURIComponent(PRINT_SHOP_SEARCH).replace(/%20/g, '+');
  return `https://www.google.com/maps/search/${term}/@${point.label},${MAPS_SEARCH_ZOOM}z`;
}

export function openPrintShopMaps(url, openWindow = defaultOpen) {
  if (!url) return false;
  openWindow(url);
  return true;
}

function defaultOpen(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
